import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import axios from 'axios';
import { CheckCircle, Clock, XCircle, AlertCircle, ClipboardList, Award, Download } from 'lucide-react';
import './vStyles/MyTasks.css';

interface MyTask {
  id: number;
  title: string;
  project_name: string;
  status: string;
  applied_at: string;
  due_date?: string;
  whatsapp_url?: string;
  has_review?: boolean;
}

interface Certificate {
  id: number;
  volunteer_id: number;
  project_id: number;
  volunteer_name: string;
  project_name: string;
  issue_date: string;
  completed_tasks_count: number;
  created_at: string;
  updated_at: string;
}

const MyTasks: React.FC = () => {
  const [myTasks, setMyTasks] = useState<MyTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MyTask | null>(null);
  const [rating, setRating] = useState(5);
  const [reviewContent, setReviewContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [certificatesLoading, setCertificatesLoading] = useState(true);
  const { volunteerId } = useAuth();

  const fetchMyTasks = useCallback(async () => {
    try {
      if (!volunteerId) {
        setError('Please log in to view your tasks');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/volunteer/${volunteerId}/my-tasks`, {
        headers: {
          'user-id': volunteerId.toString(),
          'Content-Type': 'application/json'
        }
      });

      setMyTasks(response.data as MyTask[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching my tasks:', err);
      setError('Failed to load your tasks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [volunteerId]);

  const fetchCertificates = useCallback(async () => {
    try {
      if (!volunteerId) {
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/volunteer/${volunteerId}/certificates`);
      setCertificates(response.data as Certificate[]);
    } catch (err) {
      console.error('Error fetching certificates:', err);
    } finally {
      setCertificatesLoading(false);
    }
  }, [volunteerId]);

  const handleWithdrawTask = async (taskId: number) => {
    try {
      if (!volunteerId) {
        alert('Please log in to withdraw from tasks');
        return;
      }
      
      if (!window.confirm('Are you sure you want to withdraw from this task?')) {
        return;
      }
      
      await axios.put(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/volunteer/${volunteerId}/tasks/${taskId}/withdraw`, {}, {
        headers: {
          'user-id': volunteerId.toString(),
          'Content-Type': 'application/json'
        }
      });
      
      alert('Successfully withdrew from the task.');
      fetchMyTasks(); // Refresh data
      
    } catch (error) {
      console.error('Error withdrawing from task:', error);
      alert('Failed to withdraw from task. Please try again.');
    }
  };

  const handleReviewClick = (task: MyTask) => {
    setSelectedTask(task);
    setRating(5);
    setReviewContent('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedTask || !volunteerId) return;
    
    setIsSubmitting(true);
    try {
      // Get volunteer name for the review
      const volunteerResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/volunteers/${volunteerId}`);
      const volunteer = volunteerResponse.data as { firstname: string; surname: string };
      const volunteerName = `${volunteer.firstname} ${volunteer.surname}`;

      await axios.post(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/reviews`, {
        volunteer_id: volunteerId,
        volunteer_name: volunteerName,
        task_id: selectedTask.id,
        task_name: selectedTask.title,
        rating: rating,
        review_content: reviewContent,
        is_anonymous: false,
        is_approved: true
      });

      // Update the task to mark it as reviewed
      setMyTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === selectedTask.id ? { ...task, has_review: true } : task
        )
      );

      setShowReviewModal(false);
      alert('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchMyTasks();
    fetchCertificates();
  }, [fetchMyTasks, fetchCertificates]);

  const handleDownloadCertificate = async (certificateId: number, projectName: string) => {
    try {
      const response = await axios.get<Blob>(
        `${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/certificates/${certificateId}/download`,
        {
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${projectName.replace(/\s+/g, '_')}_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={20} className="status-icon completed" />;
      case 'ongoing':
        return <Clock size={20} className="status-icon ongoing" />;
      case 'rejected':
        return <XCircle size={20} className="status-icon rejected" />;
      case 'applied':
        return <AlertCircle size={20} className="status-icon applied" />;
      default:
        return <AlertCircle size={20} className="status-icon applied" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'ongoing':
        return 'Ongoing';
      case 'rejected':
        return 'Rejected';
      case 'applied':
        return 'Applied';
      default:
        return 'Applied';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="my-tasks-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-tasks-container">
        <div className="error-container">
          <AlertCircle size={48} className="error-icon" />
          <h3>Error Loading Tasks</h3>
          <p>{error}</p>
          <button onClick={fetchMyTasks} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-tasks-container">
      <div className="my-tasks-header">
        <h1>My Tasks</h1>
        <p>View and manage your assigned tasks</p>
      </div>

      {myTasks.length === 0 ? (
        <div className="no-tasks">
          <ClipboardList size={64} className="no-tasks-icon" />
          <h3>No Tasks Yet</h3>
          <p>You haven't been assigned any tasks yet.</p>
          <p>Apply for tasks to see them here!</p>
        </div>
      ) : (
        <div className="tasks-grid">
          {myTasks.map(task => (
            <div key={task.id} className={`task-card ${task.status}`}>
              <div className="task-header">
                <h3>{task.title}</h3>
                <div className="task-status">
                  {getStatusIcon(task.status)}
                  <span className="status-text">{getStatusText(task.status)}</span>
                </div>
              </div>
              
              <div className="task-details">
                <p className="project-name">
                  <strong>Project:</strong> {task.project_name}
                </p>
                <p className="applied-date">
                  <strong>Applied:</strong> {formatDate(task.applied_at)}
                </p>
                {task.due_date && (
                  <p className="due-date">
                    <strong>Due:</strong> {formatDate(task.due_date)}
                  </p>
                )}
              </div>

              <div className="task-actions">
                {task.status === 'ongoing' && (
                  <button 
                    onClick={() => handleWithdrawTask(task.id)}
                    className="withdraw-btn"
                    title="Withdraw from this task"
                  >
                    Withdraw
                  </button>
                )}
                {task.status === 'completed' && !task.has_review && (
                  <button 
                    onClick={() => handleReviewClick(task)}
                    className="review-btn"
                    title="Review this task"
                  >
                    Review Task
                  </button>
                )}
                {task.status === 'completed' && task.has_review && (
                  <span className="reviewed-badge">Reviewed</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Certificate Awards Section */}
      <div className="certificates-section">
        <div className="section-header">
          <Award size={32} className="section-icon" />
          <div>
            <h2>Certificate Awards</h2>
            <p>Achievements and recognition certificates</p>
          </div>
        </div>

        {certificatesLoading ? (
          <div className="loading-certificates">
            <div className="loading-spinner"></div>
            <p>Loading certificates...</p>
          </div>
        ) : certificates.length === 0 ? (
          <div className="no-certificates">
            <Award size={48} className="no-certificates-icon" />
            <h3>No Certificates Yet</h3>
            <p>Complete tasks in projects to earn certificates!</p>
          </div>
        ) : (
          <div className="certificates-grid">
            {certificates.map(certificate => (
              <div key={certificate.id} className="certificate-card">
                <div className="certificate-header">
                  <Award size={24} className="certificate-icon" />
                  <h3>{certificate.project_name}</h3>
                </div>
                
                <div className="certificate-details">
                  <p className="certificate-achievement">
                    <strong>Awarded to:</strong> {certificate.volunteer_name}
                  </p>
                  <p className="certificate-tasks">
                    <strong>Completed Tasks:</strong> {certificate.completed_tasks_count}
                  </p>
                  <p className="certificate-date">
                    <strong>Issued:</strong> {formatDate(certificate.issue_date)}
                  </p>
                </div>

                <div className="certificate-actions">
                  <button 
                    onClick={() => handleDownloadCertificate(certificate.id, certificate.project_name)}
                    className="download-certificate-btn"
                    title="Download certificate as PDF"
                  >
                    <Download size={16} />
                    <span>Download Certificate</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedTask && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2>Review Task</h2>
              <button className="close-modal-btn" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="review-modal-content">
              <div className="review-task-info">
                <h3>{selectedTask.title}</h3>
                <p>Project: {selectedTask.project_name}</p>
              </div>
              
              <div className="rating-section">
                <label>Rate your experience:</label>
                <div className="rating-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`star-btn ${star <= rating ? 'active' : ''}`}
                      onClick={() => setRating(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <span className="rating-text">{rating} out of 5</span>
              </div>

              <div className="review-content-section">
                <label>Your review (optional):</label>
                <textarea
                  className="review-textarea"
                  placeholder="Share your experience..."
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="review-modal-actions">
                <button 
                  className="submit-review-btn"
                  onClick={handleSubmitReview}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Post Review'}
                </button>
                <button 
                  className="cancel-review-btn"
                  onClick={() => setShowReviewModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTasks;
